import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import GeomodView from './GeomodView'
import { MousePosition, ScaleLine, OverviewMap } from 'ol/control'
import { get as getProjection, getPointResolution } from 'ol/proj'
import GeoJSON from 'ol/format/GeoJSON';
import {
  DoubleClickZoom,
  DragPan,
  PinchZoom,
  MouseWheelZoom,
  DragZoom
} from 'ol/interaction'
import GeomodLayerGroup from './layers/GeomodLayerGroup'
import LayerGroup from 'ol/layer/Group'
import MagnifyingGlass from './utils/controls/MagnifyingGlass'
import { COORDINATE_FORMAT } from './utils/constantes'
import { toLonLat, fromLonLat, toStringCoordinates } from './utils/projection'
import copy from 'copy-to-clipboard'
import { toast } from 'vue3-toastify'
import Request from './utils/request.js';
import { MAP_DATABASE_UPDATED_EVENT_TYPE, CLEAR_OL_CACHE_EVENT_TYPE, COLOR_CHANGE_EVENT_TYPE, ONLINE_CHANGE_EVENT_TYPE, DELETE_TILE_CACHE_EVENT } from './utils/events.js'
import { DATABASE_VERSION_PARAM, LIGHT_CONDITION_PARAM } from './sources/GeomodSourceUtils'
import RadialMenuOverlay from './utils/overlay/RadialMenuOverlay'
import { TileLayerCacheStrategy } from './utils/cache'
import { setGUIColor } from './utils/GuiColor'
import { httpRequest } from './utils/httpRequest.js'

export default class GeomodMap extends Map {
  constructor(params = {}) {
    if (params.map === undefined) {
      super()
      return
    }

    const view = new GeomodView({ center: [0, 0], maxZoom: 22, zoom: 6, constrainResolution: true })
    const mapParams = {
      maxTilesLoading: params.map.maxTilesLoading,
      view,
      layers: [],
      controls: [],
      interactions: [
        new DoubleClickZoom(),
        new DragPan(),
        new PinchZoom(),
        new MouseWheelZoom(),
        new DragZoom()
      ]
    }

    super(mapParams)

    this.sourceUrl = `${params.sourceUrl}`
    this.defaultUrlSource = `${this.sourceUrl}${params.map.defaultUrlSource}`
    this.view = view
    this.initialLayerGroup_ = null

    this.geojson_ = new GeoJSON({ featureProjection: view.getProjection() });
    this.on('change:view', () => { this.geojson_.defaultFeatureProjection = getProjection(this.getView().getProjection()); });

    this.initEvent()
    this.initExtraParameters(params.defaultExtraParameters)

    this.gfiModule_ = null;
    // if (params.controls.getFeatureInfo) {
      // this.gfiModule_ = new GetFeatureInfo(this);
      // this.addControl(this.gfiModule_);
      // this.gfiModule_.on(FeatureCollectionPanelEventType.SELECTED_FEATURE, e => this.dispatchEvent(e));
    // }
    // if (params.controls.getFeatureInfoSimplifier && Array.isArray(params.controls.getFeatureInfoSimplifier)) {
    //   const getFeatureInfoSimplifier = new GetFeatureInfoSimplifier(this, mapLayers.getLayerByTag(params.controls.getFeatureInfoSimplifier.layers))
    //   this.addControl(getFeatureInfoSimplifier)
    // }

    this.magnifyingGlass_ = null

    this.menu_ = new RadialMenuOverlay()
    this.addOverlay(this.menu_)

    this.moduleLayerGroup_ = new LayerGroup()
    this.addLayer(this.moduleLayerGroup_)

    this.requestModule_ = new Request({ map: this })

    document.addEventListener(CLEAR_OL_CACHE_EVENT_TYPE, () => this.clearOLCache())
    document.addEventListener(COLOR_CHANGE_EVENT_TYPE, () => {
      const layerColor = localStorage.getItem('layerColor')
      const online = localStorage.getItem('online')
      setGUIColor(layerColor, online)
      this.clearOLCache()
    })
    document.addEventListener(ONLINE_CHANGE_EVENT_TYPE, () => {
      const layerColor = localStorage.getItem('layerColor')
      const online = typeof localStorage.getItem('online') === 'boolean' ? localStorage.getItem('online') : localStorage.getItem('online') === 'true'
      setGUIColor(layerColor, online)
    })
    document.addEventListener(DELETE_TILE_CACHE_EVENT, async () => await this.deleteCache())
    // this.on('change:view', () => this.requestModule_.updateViewProjection())
  }

  setMapViewOptions(options) {
    const storedOptions = JSON.parse(localStorage.getItem('mapView'))
    this.view.setZoom(Number(storedOptions?.zoom) || options?.zoom || 6)
    this.view.setMaxZoom(Number(storedOptions?.maxZoom) || options?.maxZoom || 22)
    this.view.setMinZoom(Number(storedOptions?.minZoom) || options?.minZoom || 0)
    const center = storedOptions?.center.map(c => Number(c)) || options?.center.map(c => Number(c)) || [0.1859, 49.4352]
    this.view.setCenter(fromLonLat(center))
    if (!storedOptions) this.changeView()
  }

  initExtraParameters(extraParameters = {}) {
    // init coordinateFormat
    const coords = localStorage.getItem('coordinateFormat')
    if (!coords) {
      const newCoords = extraParameters.coordinateFormat || COORDINATE_FORMAT.DEGREE_MINUTE_DECIMAL
      localStorage.setItem('coordinateFormat', newCoords)
    }

    // Init extra options
    const lep = JSON.parse(localStorage.getItem('layerExtraParameters')) || {}
    if (extraParameters.coordinateFormat) delete extraParameters.coordinateFormat
    const newLep = { ...extraParameters, ...lep }
    localStorage.setItem('layerExtraParameters', JSON.stringify(newLep))
  }

  setMapLayers(layers) {
    for (const layerParam of layers) {
      if (layerParam.maxScale) layerParam.maxResolution = this.view.getResolutionForZoom(layerParam.maxScale - 1)
      if (layerParam.type === 'TileWMTS') layerParam.projection = this.view.getProjection()
      if (layerParam.source && !layerParam.source.startsWith(this.sourceUrl)) layerParam.source = `${this.sourceUrl}${layerParam.source}`
      if (layerParam.visible) layerParam.visible = true
      else layerParam.visible = false
    }
    const mapLayers = new GeomodLayerGroup({ layers: this.cloneLayerParams(layers), defaultUrl: this.defaultUrlSource })
    const layerColor = localStorage.getItem('layerColor')
    for (const layer of mapLayers.getLayersArray()) {
      const mapDBVersion = localStorage.getItem('mapDBVersion') || -1;
      layer.addParam(DATABASE_VERSION_PARAM, mapDBVersion);
      document.addEventListener(MAP_DATABASE_UPDATED_EVENT_TYPE, () => layer.updateParam(DATABASE_VERSION_PARAM, localStorage.getItem('mapDBVersion')));
      if (layer.updateParam) layer.updateParam(LIGHT_CONDITION_PARAM, layerColor);
      else if (layer.setLightCondition) layer.setLightCondition(layerColor);
    }
    this.initialLayerGroup_ = mapLayers
    this.setLayerGroup(mapLayers)

    this.magnifyingGlass_ = {
      layers: this.cloneLayerParams(layers),
      defaultUrl: this.defaultUrlSource
    }
  }

  setRestraintArea(area) {
    this.getView().set('extent', area.extent)
  }

  cloneLayerParams(layerParams) {
    const clonedParams = JSON.parse(JSON.stringify(layerParams))
    for (let i = 0; i < layerParams.length; i++) {
      clonedParams[i].projection = layerParams[i].projection
    }
    return clonedParams
  }

  getInitialLayerGroup() {
    return this.initialLayerGroup_
  }

  addStyleOnControlLayer(cssClass) {
    this.getStopEventOverlayContainer_().classList.add(cssClass);
  }

  removeStyleOnControlLayer(cssClass) {
    this.getStopEventOverlayContainer_().classList.remove(cssClass);
  }

  getStopEventOverlayContainer_() {
    return this.getTargetElement().querySelector('.ol-overlaycontainer-stopevent');
  }

  getOverviewMap() {
    return this.overviewMapCtrl;
  }

  getGeoJSONParser() {
    return this.geojson_;
  }

  changeView() {
    let center = this.view.getCenter()
    center = toLonLat(center)
    const mapView = {
      center,
      minZoom: this.view.getMinZoom(),
      maxZoom: this.view.getMaxZoom(),
      zoom: this.view.getZoom()
    }
    localStorage.setItem('mapView', JSON.stringify(mapView))
  }

  initEvent() {
    this.on('moveend', this.changeView.bind(this))
    this.setCatchClickEvent();
    this.on('singleclick', event => this.singleClickCatcher_(event));
  }

  initControls(controls) {
    // Control mouse position
    if (controls.mousePosition.enabled) this.addMousePositionControl()
    // Control scale line
    if (controls.scaleLine.enabled) this.addScaleLineControl()
    // Control OverviewMap
    this.overviewMapCtrl = null
    if (controls.overview.enabled && Array.isArray(controls.overview.layers)) this.addOverviewControl(controls.overview.layers)
  }

  addMousePositionControl() {
    const mousePosition = new MousePosition({
      coordinateFormat: coord => toStringCoordinates(coord, { dmdDec: 3 }),
      projection: 'EPSG:4326',
      placeholder: 'Mouse is not over map.'
    })
    this.addControl(mousePosition)

    window.addEventListener('keydown', event => {
      if (event.ctrlKey && event.key === 'c') {
        const text = mousePosition.element.innerText
        copy(text)
        toast('Copied')
      }
    })
  }

  addScaleLineControl() {
    const minWidth = 64
    const scaleLine = new ScaleLine({ minWidth })
    fitScaleLineUnit(this.view, scaleLine, minWidth)
    this.view.on('change:resolution', fitScaleLineUnit(this.view, scaleLine, minWidth))
    this.addControl(scaleLine)
  }

  addOverviewControl(layers) {
    for (const layer of layers) {
      if (!layer.groupName) layer.groupName = 1
      layer.light = localStorage.getItem('layerColor')
    }

    const overviewLayerGroup = new GeomodLayerGroup({
      layers: layers,
      defaultUrl: this.defaultUrlSource
    })
    const overviewLayers = overviewLayerGroup.getLayersArray()
    for (const layer of overviewLayers) {
      layer.setVisible(true)
    }

    const visibleOverview = localStorage.getItem('visibleOverview')
    const _vo = typeof visibleOverview === 'boolean' ? visibleOverview : visibleOverview === 'true'

    this.overviewMapCtrl = new OverviewMap({
      className: 'ol-overviewmap passive',
      layers: overviewLayers,
      collapsed: !_vo,
      view: new View({
        center: this.getView().getCenter(),
        zoom: this.getView().getZoom() - 4,
        minZoom: 3,
        maxZoom: 13,
        projection: this.getView().getProjection()
      })
    })

    this.addControl(this.overviewMapCtrl)
  }

  getMagnifyingGlass() {
    return this.magnifyingGlass_ instanceof MagnifyingGlass ? this.magnifyingGlass_ : undefined;
  }

  getFlatLayers() {
    return [...this.getLayers().getArray(), ...this.moduleLayerGroup_.getLayers().getArray()];
  }

  getCatchClickEvent() {
    return this.singleClickCatcher_;
  }

  setCatchClickEvent(listener) {
    const tmplistener = (listener ? listener : (e) => {
      const event = new CustomEvent('getFeaturesInfo', { detail: { coord: e.coordinate } })
      document.dispatchEvent(event)
    })
    this.singleClickCatcher_ = tmplistener;
  }

  getRequestModule() {
    return this.requestModule_;
  }

  addFeatureInfoSpecialDisplay(specialDisplay) {
    if (this.gfiModule_) {
      this.gfiModule_.addSpecialDisplay(specialDisplay);
    }
  }

  setGetFeatureInfoLimitation(layers) {
    if (this.gfiModule_) {
      this.gfiModule_.setRequestLayerLimitation(layers);
    }
  }

  addMagnifyingGlassControl() {
    if (!(this.magnifyingGlass_ instanceof MagnifyingGlass)) {
      const layerGroup = new GeomodLayerGroup(this.magnifyingGlass_);
      for (const layer of layerGroup.getLayersArray()) {
        const mapDBVersion = localStorage.getItem('mapDBVersion') || -1;
        layer.addParam(DATABASE_VERSION_PARAM, mapDBVersion);
        document.addEventListener(MAP_DATABASE_UPDATED_EVENT_TYPE, () => layer.updateParam(DATABASE_VERSION_PARAM, localStorage.getItem('mapDBVersion')));
      }

      this.magnifyingGlass_ = new MagnifyingGlass({
        map: this,
        layers: layerGroup,
      });

      this.addControl(this.magnifyingGlass_);
    }
    return this.magnifyingGlass_;
  }

  radialMenu(items, coords) {
    if (items) {
      this.menu_.setPosition(coords)
    }
    this.menu_.show(items)
  }

  clearOLCache() {
    for (const layer of this.getLayers().getArray()) {
      if (layer.getSource) {
        const source = layer.getSource()
        if (source.tileCache && source.hasOwnProperty('cacheStrategy') && source.cacheStrategy instanceof TileLayerCacheStrategy) {
          source.cacheStrategy.clearOLCache(source);
          source.tileCache.clear()
        } else {
          source.refresh()
        }
      }
    }
  }

  async deleteCache() {
    if (caches && await caches.has('tile-cache')) await caches.delete('tile-cache');
    const version = await this.getMapDatabaseVersion();
    localStorage.setItem('mapDBVersion', version);
    const event = new CustomEvent(MAP_DATABASE_UPDATED_EVENT_TYPE);
    document.dispatchEvent(event);
    this.clearOLCache();
    this.updateLayers()
  }

  updateLayers() {
    const layers = [...this.getLayers().getArray()]
    this.getLayers().clear()
    for (const layer of layers) {
      if (layer.get('name') && layer.get('name').includes('benc')) {
        if (layer.getSource) {
          const source = layer.getSource()
          if (source.getUrls) {
            const urls = source.getUrls()
            const newUrls = urls.map(url => {
              const urlObj = new URL(url)
              urlObj.searchParams.set('_cache', Date.now())
              return urlObj.toString()
            })
            source.setUrls(newUrls)
          }
        }
      }     
      this.addLayer(layer)
    }
  }

  async getMapDatabaseVersion() {
    try {
      const req = await httpRequest('/mapdatabase', { method: 'GET' }, true)
      let version = await req.text()
      if (version.includes('VALIDATION_DATABASE')) {
        version = version.replace('VALIDATION_DATABASE=', '')
      }
      return version
    } catch (e) {
      console.error(e)
      return '1';
    }
  }
}

function fitScaleLineUnit(view, scaleLine, minWidth) {
  const projection = view.getProjection();
  let pointResolution = getPointResolution(projection, view.getResolution(), view.getCenter(), 'metric');
  if (projection.getUnits() !== 'degrees' && projection.getMetersPerUnit()) {
    pointResolution *= projection.getMetersPerUnit();
  }
  const currentUnit = scaleLine.getUnits();
  let unitToDisplay;
  if (minWidth * pointResolution >= 1000) {
    unitToDisplay = 'nautical';
  } else {
    unitToDisplay = 'metric';
  }

  if (unitToDisplay !== currentUnit) {
    scaleLine.setUnits(unitToDisplay);
  }
}